import { Suspense } from 'react';

import {
	mapContentItemToArticle,
	TopVideos as TopVideosData,
} from 'services/freeform';

import { useViewport } from 'hooks/use-viewport';

import { useUserStore } from 'stores/use-user-store';

import CardFeatured from 'core/generic/components/cards/card-featured';
import CardNews from 'core/generic/components/cards/card-news';

import { NEWS_IMAGE_FALLBACK } from '@home/utils/news-image-fallback';

import { ShowMoreSection } from '../shared/show-more-section/show-more-section';
import styles from './top-videos.module.scss';

const DEFAULT_MORE_LINK = '/citytv';
const DEFAULT_ADVERT_ID = 'homepage_promo_rail_citytv';

type TopVideosProps = {
	data: TopVideosData;
};

export const TopVideos = ({ data }: TopVideosProps) => {
	const { viewportSize } = useViewport();
	const isDesktop = viewportSize === 'large' || viewportSize === 'xlarge';
	const isUserLoggedIn = useUserStore((state) =>
		Boolean(state.user?.isLoggedIn)
	);

	const lead = mapContentItemToArticle(data.topVideo);
	const others = (data.otherVideos ?? [])
		.map(mapContentItemToArticle)
		.slice(0, 4);
	const moreLink = data.moreLink || DEFAULT_MORE_LINK;
	const moreLabel = data.moreLabel || 'More video';
	const advertId = data.advertId || DEFAULT_ADVERT_ID;
	const allVideos = [lead, ...others];

	return (
		<section className={styles.root} aria-label="Top videos">
			<div className={`container ${styles.container}`}>
				<header className={styles.header}>
					<Suspense fallback={<a href={lead.url}>{lead.title}</a>}>
						<CardFeatured
							url={lead.url}
							title={lead.title}
							caption={lead.primaryTag}
							image={{
								url: lead.image.url,
								alt: lead.image.alt,
							}}
							showVideoButton={lead.isVideo}
							signInRequired={Boolean(lead.isSignInRequired)}
							premiumVideo={Boolean(lead.isPremium)}
							isUserLoggedIn={isUserLoggedIn}
						/>
					</Suspense>
				</header>

				<div
					className={`${styles.body} ${
						isDesktop ? styles['body--withAside'] : ''
					}`}
				>
					<ul className={styles.list}>
						{others.map((article, index) => (
							<li
								key={`${article.url}-${index}`}
								className={styles.listItem}
							>
								<Suspense
									fallback={
										<a href={article.url}>
											{article.title}
										</a>
									}
								>
									<CardNews
										fallback={NEWS_IMAGE_FALLBACK}
										data={article}
										size="Medium"
										type="Icon"
										direction="Column"
										fullWidth
										showVideoDuration={false}
									/>
								</Suspense>
							</li>
						))}
					</ul>

					{isDesktop ? (
						<aside
							className={styles.aside}
							aria-label="Side banner"
							data-ad-slot={advertId}
						>
							<div
								className={styles['ad-slot']}
								aria-hidden="true"
							/>
						</aside>
					) : null}
				</div>

				<ShowMoreSection link={moreLink} label={moreLabel} />

				<noscript>
					<ul aria-label="All top videos">
						{allVideos.map((video, index) => (
							<li key={`${video.url}-${index}`}>
								<a href={video.url}>{video.title}</a>
							</li>
						))}
					</ul>
				</noscript>
			</div>
		</section>
	);
};

export default TopVideos;
